import React, { useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { X, Plus } from 'lucide-react';
import clsx from 'clsx';
import { Entry } from '../lib/supabase';

interface DayDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date;
  entries: Entry[];
  onAddEntry: () => void;
  onEditEntry: (entry: Entry) => void;
}

export default function DayDetailModal({
  isOpen,
  onClose,
  date,
  entries: unsortedEntries,
  onAddEntry,
  onEditEntry,
}: DayDetailModalProps) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const entries = [...unsortedEntries].sort((a, b) => {
    if (a.invoiced !== b.invoiced) return a.invoiced ? -1 : 1;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  return (
    <div className="fixed inset-0 z-40">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div
        className={clsx(
          'relative mx-auto h-full sm:h-auto sm:mt-8',
          'w-full sm:max-w-md',
          'bg-white shadow-xl',
          'sm:rounded-lg',
          'overflow-y-auto',
          'animate-zoom-in'
        )}
      >
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {format(date, 'MMMM d, yyyy')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 p-2 -mr-2"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-2 pb-20">
            {entries.map((entry) => (
              <button
                key={entry.id}
                onClick={() => onEditEntry(entry)}
                className={clsx(
                  'w-full flex items-center gap-3',
                  'bg-gray-50 hover:bg-gray-100',
                  'rounded-lg px-4 py-3',
                  'text-left transition-colors'
                )}
              >
                <span
                  className={clsx(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    entry.invoiced ? 'bg-green-500' : 'bg-indigo-500'
                  )}
                />
                <span className="text-base font-medium text-gray-900">
                  {entry.delivery_note_number || 'No DN'}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="fixed bottom-4 left-4 right-4 sm:absolute sm:bottom-6 sm:left-6 sm:right-6">
          <button
            onClick={onAddEntry}
            className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Entry
          </button>
        </div>
      </div>
    </div>
  );
}
